import {
  ArchCheckTopicsResponse,
  ArchCourseForTable,
  ArchModule,
  ArchTopic,
} from 'apis/entities/architecture.entity'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import { useApi } from 'providers/ApiProvider'
import { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react'
import { useCompositeAuth } from 'providers/CompositeAuthProvider'
import {
  focusAndSelectField,
  InlineEditButtons,
} from 'components/InlineEditControls'
import Spinner from './Spinner'
import Svg, { Icon, IconClass } from 'components/Svg'
import Emitter, { Events } from 'core/emitter'
import {
  ArchActionType,
  getEntityLabel,
  getExportFileNameForCourse,
} from 'utils/ArchUtil'
import { ToastUtil } from 'utils/ToastUtil'
import { TextareaAutosize } from '@mui/base'
import React from 'react'
import ButtonSpinner from './ButtonSpinner'
import tickPng from 'images/check_mark_32.png'
import crossPng from 'images/cross_mark_32.png'
import placeholderPng from 'images/placeholder-32.png'
import { Tooltip } from 'react-tooltip'
import { tooltipStyle } from 'utils/TableUtils'
import WidgetArchCheckTopicsResult from './WidgetArchCheckTopicsResult'
import { Quiz } from 'apis/entities/quiz.entity'
import { getDateTimeString } from 'utils/StringUtil'
import { QuizStatusText } from './QuizStatusText'
import UpdateTopicLinksModal from './UpdateTopicLinksModal'
import { Role } from 'providers/CompositeAuthProvider'
import { getTopicUpdatedMessage } from 'utils/TopicToastUtil'
import { downloadBlobAsFile } from 'utils/DownloadUtil'
import { renderUrlToPdf } from 'utils/PdfServerUtil'
import { useHoverDropdown } from 'hooks/useHoverDropdown'
import JSZip from 'jszip'

interface ArchCourseDetailsViewProps {
  course: ArchCourseForTable
  isEnterprise?: boolean
  onClose: () => void
}

const UNSWO_HEADER_BG = '#FFDC00'

// prefixes the filename with the same "module.topic" numbering shown
// in the first column of the topic list (e.g. "1.3"), not a flat index.
const pdfFilenameForTopic = (topicNumber: string, topic: ArchTopic): string => {
  const safeName = (topic.name || topic.id).replace(/[\\/:*?"<>|]/g, '-')
  return `${topicNumber}-${safeName}.pdf`
}

// some topics have been seen with link literally set to the string
// "undefined" (a data bug elsewhere, not something we can fix from here) —
// guard against sending that (or any other unparseable value) to the pdf
// server, which would otherwise 500 on every request.
const isValidTopicLink = (link?: string): boolean => {
  if (!link) return false
  try {
    const url = new URL(link)
    return !!url
  } catch {
    return false
  }
}

// pulls the document id out of a topic link (.../lx/{id}/index.html),
// so its print.pdf status can be looked up server-side. Checking via a direct
// browser HEAD to the content domain doesn't work — those buckets don't allow
// CORS from arbitrary frontend origins.
const getDocumentIdFromTopicLink = (link: string): string | null => {
  try {
    const { pathname } = new URL(link)
    const match = pathname.match(/\/lx\/([^/]+)\//)
    return match ? match[1] : null
  } catch {
    return null
  }
}

type PdfStatus = 'checking' | 'yes' | 'no' | 'unknown'

export function QuizName({ quiz }: { quiz: Quiz }) {
  if (quiz.fileUrl) {
    return (
      <a
        className="arch-topic-list-item-link"
        href={quiz.fileUrl}
        target="_blank"
        rel="noreferrer"
      >
        {quiz.name}
      </a>
    )
  }

  return (
    <span
      className="arch-topic-list-item-link"
      style={{ textDecorationLine: 'none' }}
    >
      {quiz.name}
    </span>
  )
}

export default function WidgetArchCourseDetailsView({
  course,
  isEnterprise = true,
  onClose,
}: ArchCourseDetailsViewProps) {
  const { isLogged, getRole } = useCompositeAuth()
  const role = getRole()
  const isAdmin = role === Role.Admin || role === Role.SuperAdmin
  const canCopy = role === Role.SuperAdmin
  const {
    getArchCourse,
    updateArchTopic,
    exportArchExcel,
    checkTopics,
    getCheckTopicsResults,
    checkDumpZipExists,
    uploadCourseDump,
  } = useArchitectureApi()
  const {
    checkPrintPdfExistsBatch,
    downloadPrintPdf,
    getPrintPdfPresignedUrl,
  } = useApi()
  const [modules, setModules] = useState<ArchModule[]>([])
  const [quizzes, setQuizzes] = useState<Quiz[]>([])
  const [isFetchingCourse, setIsFetchingCourse] = useState(false)
  const [highlightTopicIds, setHighlightTopicIds] = useState<string[]>([])
  const [collapsedModuleIds, setCollapsedModuleIds] = useState<string[]>([])
  const refTimeoutDict = useRef<{ [index: string]: NodeJS.Timeout }>({})
  const refEditTextareas = useRef<
    Map<string, HTMLTextAreaElement | HTMLInputElement>
  >(new Map())
  const timeoutForSave = 500
  const [editedModeCells, setEditedModeCells] = useState<{
    [id: string]: Boolean
  }>({})
  const [isCheckingTopics, setIsCheckingTopics] = useState(false)
  const actionsMenuRef = useRef<HTMLDivElement>(null)
  const {
    isOpen: isActionsMenuOpen,
    open: openActionsMenu,
    close: closeActionsMenu,
    forceClose: forceCloseActionsMenu,
  } = useHoverDropdown()
  const [result, setResult] = useState<ArchCheckTopicsResponse | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [checkTopicsResult, setCheckTopicsResult] =
    useState<ArchCheckTopicsResponse | null>(null)
  const [isCollapsedAssessments, setIsCollapsedAssessments] = useState(false)
  const [showUpdateLinksModal, setShowUpdateLinksModal] = useState(false)
  const [isDumping, setIsDumping] = useState(false)
  const [dumpProgress, setDumpProgress] = useState<{
    done: number
    total: number
  } | null>(null)
  const [dumpZipStatus, setDumpZipStatus] = useState<PdfStatus>('checking')
  const unswoHeaderStyle = !isEnterprise
    ? { background: UNSWO_HEADER_BG, color: '#000' }
    : undefined
  const moduleLabel = getEntityLabel(ArchActionType.Module, !isEnterprise)
  const loLabel = isEnterprise ? 'Learning outcome' : 'Purpose'
  const headerFillIconClass = isEnterprise
    ? IconClass.FillWhite
    : IconClass.FillBlackSolid
  const headerStrokeIconClass = isEnterprise
    ? IconClass.StrokeWhite
    : IconClass.StrokeBlackSolid

  const handleKeyUpForTextArea = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
    }
  }

  const resetTimeout = (
    index: string,
    timeout?: NodeJS.Timeout | undefined,
  ) => {
    if (refTimeoutDict.current[index]) {
      clearTimeout(refTimeoutDict.current[index])
    }
    if (timeout !== undefined && timeout !== null) {
      refTimeoutDict.current[index] = timeout
    }
  }

  const updateLocalTopicLearningOutcome = useCallback(
    (topicId: string, learningOutcome: string) => {
      const newModules = [...modules]
      const moduleIndex = newModules.findIndex((m) =>
        m.topics.some((t) => t.id === topicId),
      )
      if (moduleIndex === -1) {
        return
      }
      const module = newModules[moduleIndex]
      const topicIndex = module.topics.findIndex((t) => t.id === topicId)
      if (topicIndex === -1) {
        return
      }
      const newTopics = [...module.topics]
      newTopics[topicIndex].learningOutcome = learningOutcome
      module.topics = newTopics
      setModules(newModules)
    },
    [modules],
  )

  const updateLocalTopicUpdatedAt = useCallback(
    (topicId: string, updatedAt: Date) => {
      const newModules = [...modules]
      const moduleIndex = newModules.findIndex((m) =>
        m.topics.some((t) => t.id === topicId),
      )
      if (moduleIndex === -1) {
        return
      }
      const module = newModules[moduleIndex]
      const topicIndex = module.topics.findIndex((t) => t.id === topicId)
      if (topicIndex === -1) {
        return
      }
      const newTopics = [...module.topics]
      newTopics[topicIndex].updatedAt = updatedAt
      module.topics = newTopics
      setModules(newModules)
    },
    [modules],
  )

  const updateLocalTopicDuration = useCallback(
    (topicId: string, durationSeconds: number) => {
      const newModules = [...modules]
      const moduleIndex = newModules.findIndex((m) =>
        m.topics.some((t) => t.id === topicId),
      )
      if (moduleIndex === -1) {
        return
      }
      const module = newModules[moduleIndex]
      const topicIndex = module.topics.findIndex((t) => t.id === topicId)
      if (topicIndex === -1) {
        return
      }
      const newTopics = [...module.topics]
      newTopics[topicIndex].durationSeconds = durationSeconds
      module.topics = newTopics
      setModules(newModules)
    },
    [modules],
  )

  const updateTopicLearningOutcome = useCallback(
    async (
      topicId: string,
      learningOutcome: string,
      toast: boolean = false,
    ) => {
      try {
        const topic = await updateArchTopic(
          topicId,
          undefined,
          learningOutcome,
          undefined,
        )
        if (toast) {
          ToastUtil.success(getTopicUpdatedMessage(loLabel.toLowerCase()))
        }
        // console.log('updateArchTopic result', topic)
        const updatedAt = topic.updatedAt
        // update local state
        updateLocalTopicLearningOutcome(topic.id, learningOutcome)
        updateLocalTopicUpdatedAt(topicId, updatedAt)
      } catch (error) {
        ToastUtil.error(`Update ${loLabel.toLowerCase()} failed`)
        console.error(error)
      }
    },
    [
      loLabel,
      updateArchTopic,
      updateLocalTopicLearningOutcome,
      updateLocalTopicUpdatedAt,
    ],
  )

  const updateTopicDuration = useCallback(
    async (topicId: string, minutes: string, toast: boolean = false) => {
      try {
        const parsed = parseInt(minutes, 10)
        if (isNaN(parsed) || parsed < 0) {
          ToastUtil.warning('Duration must be a valid number of minutes')
          return
        }
        const topic = await updateArchTopic(
          topicId,
          undefined,
          undefined,
          undefined,
          parsed * 60,
        )
        if (toast) {
          ToastUtil.success(getTopicUpdatedMessage('duration'))
        }
        updateLocalTopicDuration(topic.id, parsed * 60)
        updateLocalTopicUpdatedAt(topicId, topic.updatedAt)
      } catch (error) {
        ToastUtil.error('Topic update failed')
        console.error(error)
      }
    },
    [updateArchTopic, updateLocalTopicDuration, updateLocalTopicUpdatedAt],
  )

  const fetchCheckTopicsResults = useCallback(async () => {
    try {
      const res = await getCheckTopicsResults(course.id)
      setCheckTopicsResult(res)
    } catch (error) {
      console.error(error)
    }
  }, [course.id, getCheckTopicsResults])

  const fetchCourse = useCallback(async () => {
    try {
      setIsFetchingCourse(true)
      const res = await getArchCourse(course.id)
      setModules(res.modules || [])
      setQuizzes(res.quizzes || [])
    } catch (error) {
      console.log('Error fetching course', error)
    } finally {
      setIsFetchingCourse(false)
    }
  }, [course.id, getArchCourse])

  const renderTitle = () => {
    const words: string[] = isEnterprise
      ? [course.clientName, course.programName, course.intakeName, course.name]
      : [course.programName, course.intakeName]
    const text = words.join('/')
    return <p className="arch-subheading">{text}</p>
  }

  const onMouseOverTopic = (topic: ArchTopic) => {
    // find the related module by topic id
    const module = modules.find((m) => m.topics.some((t) => t.id === topic.id))
    // find all topic ids in the module
    const topicIds = module?.topics.map((t) => t.id) || []
    setHighlightTopicIds(topicIds)
  }

  const onMouseOutTopic = () => {
    setHighlightTopicIds([])
  }

  const isHighlighted = (topicId: string) => {
    return highlightTopicIds.includes(topicId)
  }

  const getHighlightClass = (topicId: string) => {
    if (!isHighlighted(topicId)) {
      return ''
    }
    return isEnterprise
      ? 'arch-topic-list-item-highlight'
      : 'arch-topic-list-item-highlight-unswo'
  }

  const onToggleCollapseModule = (moduleId: string) => {
    if (collapsedModuleIds.includes(moduleId)) {
      setCollapsedModuleIds((prev) => prev.filter((id) => id !== moduleId))
    } else {
      setCollapsedModuleIds((prev) => [...prev, moduleId])
    }
  }

  const isCollapsed = (moduleId: string) => {
    return collapsedModuleIds.includes(moduleId)
  }

  const setInlineEditMode = (
    prefix: string,
    topicId: string,
    value: boolean,
  ) => {
    setEditedModeCells((prev) => ({
      ...prev,
      [`${prefix}-${topicId}`]: value,
    }))
  }

  const onDuplicate = (id: string, type: ArchActionType) => {
    Emitter.emit(Events.ShowArchCopy, { type, ids: [id] })
  }

  const openTopicEditModal = (
    topic: ArchTopic,
    position: { x: number; y: number },
  ) => {
    Emitter.emit(Events.ShowArchEditTopic, {
      type: ArchActionType.Topic,
      topic,
      position,
    })
  }

  const renderModule = (module: ArchModule, index: number) => {
    const moduleDisplayName = `${moduleLabel.toLowerCase()} ${index + 1}`

    return (
      <React.Fragment key={`${module.id}-${index}`}>
        <li
          key={module.id}
          className="arch-module-list-item flex flex-row items-center justify-between"
          style={unswoHeaderStyle}
          data-testid={`arch-module-item-${module.id}`}
        >
          <div className="pl-[40px]">
            <span className="whitespace-nowrap">
              {moduleLabel} {index + 1}: {module.name}
            </span>
          </div>

          <div className="flex flex-row items-center gap-[12px] pr-[24px]">
            {canCopy && (
              <button
                type="button"
                aria-label={`Duplicate ${moduleDisplayName}`}
                onClick={() => onDuplicate(module.id, ArchActionType.Module)}
              >
                <Svg icon={Icon.Copy} className={headerStrokeIconClass} />
              </button>
            )}
            <button
              type="button"
              aria-label={`${
                isCollapsed(module.id) ? 'Expand' : 'Collapse'
              } ${moduleDisplayName}`}
              onClick={() => onToggleCollapseModule(module.id)}
            >
              {isCollapsed(module.id) && (
                <Svg icon={Icon.Down} className={headerFillIconClass} />
              )}
              {!isCollapsed(module.id) && (
                <Svg icon={Icon.Up} className={headerFillIconClass} />
              )}
            </button>
          </div>
        </li>
        {module.topics.length > 0 &&
          !isCollapsed(module.id) &&
          module.topics.map((topic, topicIndex) =>
            renderTopic(topic, index, topicIndex),
          )}
      </React.Fragment>
    )
  }

  const renderQuiz = (quiz: Quiz) => {
    return (
      <li
        key={quiz.id}
        className="flex flex-row items-center gap-[12px] px-[24px] py-[4px]"
      >
        <div className="flex w-[30%] min-w-[30%] max-w-[30%] flex-row items-center gap-[12px]">
          <div className="flex grow flex-row items-center">
            <QuizName quiz={quiz} />
          </div>
        </div>
        <div className="arch-topic-last-updated-at">
          <span>Last updated:</span>
          <span>{getDateTimeString(quiz.updatedAt)}</span>
        </div>
        <QuizStatusText quiz={quiz} />
      </li>
    )
  }

  const readOnlyMode = (prefix: string, topicId: string): boolean => {
    return !editedModeCells[`${prefix}-${topicId}`]
  }

  const getResultByTopicId = (topicId: string): boolean => {
    // find the result by topic id, pass or fail
    return (
      checkTopicsResult?.results?.find((r) => r.topic.id === topicId)?.result
        .success || false
    )
  }

  const showResultIcon = (topicId: string): boolean => {
    return (
      checkTopicsResult?.results?.find((r) => r.topic.id === topicId) !==
      undefined
    )
  }

  // index.html (linking to every topic's print.pdf and the dump.zip
  // itself) lands in the scratch course-dumps bucket, not the publish bucket
  // print.pdf lives in — it's a temporary/regeneratable artifact, not real
  // published content.
  //
  // Hardcoded fallback — same reasoning as the backend's BUCKET_COURSE_DUMPS
  // / COURSE_DUMPS_URL defaults (courses.service.ts): the build pipeline's
  // env var can't be set yet (blocked on an iam:PassRole grant), so this
  // keeps the link working in the meantime. Keep this value in sync with the
  // backend's fallback if it ever changes.
  const getDumpIndexUrl = (): string => {
    const courseDumpsUrl =
      process.env.REACT_APP_COURSE_DUMPS_URL ||
      'temporary-access.dev.lifelonglearning.ext-aws.unsw.edu.au'
    return `https://${courseDumpsUrl}/course-exports/${course.id}/index.html`
  }

  // no point showing a dump.zip status icon if there's nothing in the
  // course that could ever produce one.
  const hasAnyValidTopicLink = modules.some((m) =>
    m.topics.some((t) => isValidTopicLink(t.link)),
  )

  // last two tokens of the breadcrumb (intake, then course) so the
  // downloaded filename disambiguates same-named courses across intakes,
  // e.g. "June 2026 - Course 1.zip" instead of just "Course 1.zip".
  const getDumpZipFilename = (): string => {
    const name = [course.intakeName, course.name || course.id]
      .filter(Boolean)
      .join(' - ')
    const safeName = name.replace(/[\\/:*?"<>|]/g, '-')
    return `${safeName}.zip`
  }

  const onCopyDumpZipUrl = async (successMessage = 'Link copied') => {
    try {
      await navigator.clipboard.writeText(getDumpIndexUrl())
      ToastUtil.success(successMessage)
    } catch (err) {
      console.error('Failed to copy index.html link', err)
      ToastUtil.error('Failed to copy link')
    }
  }

  const getResultIcon = (topicId: string): string => {
    const topic = modules.flatMap((m) => m.topics).find((t) => t.id === topicId)
    if (!topic) {
      return placeholderPng
    }
    // compare the updatedAt of the topic with the result
    const resultUpdatedAt = checkTopicsResult?.updatedAt
    if (resultUpdatedAt && topic.updatedAt > resultUpdatedAt) {
      return placeholderPng
    }

    if (!showResultIcon(topicId)) {
      return placeholderPng
    }
    return getResultByTopicId(topicId) ? tickPng : crossPng
  }

  const renderTopic = (
    topic: ArchTopic,
    moduleIndex: number,
    topicIndex: number,
  ) => {
    const topicNumber = `${moduleIndex + 1}.${topicIndex + 1}`
    const canEditTopicFields = isAdmin && !course.intakeLocked
    const learningOutcomeReadOnly =
      !canEditTopicFields || readOnlyMode('learning-outcome', topic.id)
    const topicDurationReadOnly =
      !canEditTopicFields || readOnlyMode('topic-duration', topic.id)

    return (
      <li
        key={topic.id}
        className={`flex flex-row items-center gap-[12px] px-[24px] py-[4px] ${getHighlightClass(
          topic.id,
        )}`}
        onMouseOver={() => onMouseOverTopic(topic)}
        onMouseOut={onMouseOutTopic}
        data-testid={`arch-topic-item-${topic.id}`}
      >
        <div className="flex w-[30%] min-w-[30%] max-w-[30%] flex-row items-center gap-[12px]">
          {checkTopicsResult && checkTopicsResult.results && (
            <img
              src={getResultIcon(topic.id)}
              width="16"
              alt="result"
              data-tooltip-id={`tooltip-topic-result-${topic.id}`}
            />
          )}
          <div className="arch-topic-list-item-title">{topicNumber}</div>
          <div className="flex grow flex-row items-center">
            {isValidTopicLink(topic.link) && (
              <a
                className="arch-topic-list-item-link"
                href={topic.link}
                target="_blank"
                rel="noreferrer"
              >
                {topic.name}
              </a>
            )}
            {!isValidTopicLink(topic.link) && (
              <TextareaAutosize
                style={{ textDecorationLine: 'none' }}
                className="arch-topic-list-item-link w-full"
                value={topic.name}
                readOnly={true}
                onDoubleClick={(e) => {
                  if (!isAdmin || course.intakeLocked) return
                  openTopicEditModal(topic, {
                    x: e.clientX,
                    y: e.clientY,
                  })
                }}
                placeholder="Type topic name here"
              />
            )}
          </div>
          {canEditTopicFields && (
            <button
              className="min-w-[17px]"
              onClick={(e) => {
                openTopicEditModal(topic, {
                  x: e.clientX,
                  y: e.clientY,
                })
              }}
            >
              <Svg icon={Icon.Edit} className={IconClass.FillTertiaryTeal} />
            </button>
          )}
        </div>
        <div className="arch-topic-last-updated-at">
          <span>Last updated:</span>
          <span>{getDateTimeString(topic.updatedAt)}</span>
        </div>
        <div className="flex w-full flex-row items-center gap-1">
          <span className="arch-read-only-small">
            {isEnterprise ? 'LO:' : `${loLabel}:`}
          </span>
          <TextareaAutosize
            ref={(el: HTMLTextAreaElement | null) => {
              if (el) {
                refEditTextareas.current.set(`learning-outcome-${topic.id}`, el)
              } else {
                refEditTextareas.current.delete(`learning-outcome-${topic.id}`)
              }
            }}
            className={`mentem-scrollbar-2 w-full ${
              learningOutcomeReadOnly
                ? 'arch-read-only-small'
                : 'arch-add-input-small'
            }`}
            defaultValue={topic.learningOutcome}
            placeholder={`Type ${loLabel.toLowerCase()} here`}
            readOnly={learningOutcomeReadOnly}
            maxRows={2}
            onDoubleClick={(e) => {
              if (!canEditTopicFields) return
              setInlineEditMode('learning-outcome', topic.id, true)
              focusAndSelectField(e.currentTarget)
            }}
            onBlur={() =>
              setInlineEditMode('learning-outcome', topic.id, false)
            }
            onKeyDown={handleKeyUpForTextArea}
            onKeyUp={async (e) => {
              if (e.key === 'Enter') {
                const value = e.currentTarget.value
                if (value === topic.learningOutcome) {
                  return
                }
                if (value !== topic.learningOutcome) {
                  resetTimeout(`topic-learning-outcome-${topic.id}`)
                  await updateTopicLearningOutcome(
                    topic.id,
                    e.currentTarget.value,
                    true,
                  )
                }
              } else {
                // auto save by x seconds
                const value = e.currentTarget.value.trim()
                if (value !== topic.learningOutcome) {
                  const timeoutId = setTimeout(async () => {
                    await updateTopicLearningOutcome(topic.id, value)
                  }, timeoutForSave)
                  resetTimeout(`topic-learning-outcome-${topic.id}`, timeoutId)
                }
              }
            }}
          />
          {canEditTopicFields && (
            <>
              {!learningOutcomeReadOnly && (
                <InlineEditButtons
                  onConfirm={async () => {
                    const el = refEditTextareas.current.get(
                      `learning-outcome-${topic.id}`,
                    )
                    const rawValue = el?.value ?? ''
                    if (!rawValue.trim()) {
                      ToastUtil.warning(`${loLabel} cannot be empty`)
                      return
                    }
                    resetTimeout(`topic-learning-outcome-${topic.id}`)
                    if (rawValue !== topic.learningOutcome) {
                      await updateTopicLearningOutcome(topic.id, rawValue, true)
                    }
                    setInlineEditMode('learning-outcome', topic.id, false)
                  }}
                  onCancel={() => {
                    const el = refEditTextareas.current.get(
                      `learning-outcome-${topic.id}`,
                    )
                    if (el) {
                      el.value = topic.learningOutcome
                    }
                    resetTimeout(`topic-learning-outcome-${topic.id}`)
                    setInlineEditMode('learning-outcome', topic.id, false)
                  }}
                />
              )}
              {learningOutcomeReadOnly && (
                <button
                  className="mr-[12px] min-w-[17px]"
                  onClick={() => {
                    setInlineEditMode('learning-outcome', topic.id, true)
                    focusAndSelectField(
                      refEditTextareas.current.get(
                        `learning-outcome-${topic.id}`,
                      ) || null,
                    )
                  }}
                >
                  <Svg
                    icon={Icon.Edit}
                    className={IconClass.FillTertiaryTeal}
                  />
                </button>
              )}
            </>
          )}
          {!isEnterprise && (
            <div className="flex flex-shrink-0 items-center gap-1">
              <span className="arch-topic-list-item-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M9 15.375C12.5208 15.375 15.375 12.5208 15.375 9C15.375 5.47918 12.5208 2.625 9 2.625C5.47918 2.625 2.625 5.47918 2.625 9C2.625 12.5208 5.47918 15.375 9 15.375Z"
                    stroke="black"
                  />
                  <path
                    d="M12.375 9H9.1875C9.13777 9 9.09008 8.98025 9.05492 8.94508C9.01975 8.90992 9 8.86223 9 8.8125V6.375"
                    stroke="black"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <input
                ref={(el: HTMLInputElement | null) => {
                  if (el) {
                    refEditTextareas.current.set(
                      `topic-duration-${topic.id}`,
                      el,
                    )
                  } else {
                    refEditTextareas.current.delete(
                      `topic-duration-${topic.id}`,
                    )
                  }
                }}
                data-testid={`input-topic-duration-${topic.id}`}
                className={`w-[44px] ${
                  topicDurationReadOnly
                    ? 'arch-topic-list-item-title'
                    : 'arch-add-input-small'
                }`}
                defaultValue={
                  topic.durationSeconds != null
                    ? Math.round(topic.durationSeconds / 60)
                    : ''
                }
                placeholder="minutes"
                readOnly={topicDurationReadOnly}
                onDoubleClick={(e) => {
                  if (!canEditTopicFields) return
                  setInlineEditMode('topic-duration', topic.id, true)
                  focusAndSelectField(e.currentTarget)
                }}
                onBlur={(e) => {
                  setInlineEditMode('topic-duration', topic.id, false)
                  const value = e.currentTarget.value.trim()
                  if (
                    value !==
                    String(
                      topic.durationSeconds != null
                        ? Math.round(topic.durationSeconds / 60)
                        : '',
                    )
                  ) {
                    updateTopicDuration(topic.id, value)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
              />
              {canEditTopicFields && (
                <button
                  data-testid={`btn-edit-duration-${topic.id}`}
                  className="min-w-[17px]"
                  onClick={() => {
                    setInlineEditMode(
                      'topic-duration',
                      topic.id,
                      readOnlyMode('topic-duration', topic.id),
                    )
                    focusAndSelectField(
                      refEditTextareas.current.get(
                        `topic-duration-${topic.id}`,
                      ) as HTMLInputElement | null,
                    )
                  }}
                >
                  <Svg
                    icon={Icon.Edit}
                    className={IconClass.FillTertiaryTeal}
                  />
                </button>
              )}
            </div>
          )}
        </div>
      </li>
    )
  }

  // TextareaAutosize default value is uncontrollable and created once
  const onUpdatedArchTopic = useCallback(
    (data: { topic: ArchTopic }) => {
      const topicId = data.topic.id
      // update local state
      const newModules = [...modules]
      const moduleIndex = newModules.findIndex((m) =>
        m.topics.some((t) => t.id === topicId),
      )
      if (moduleIndex === -1) {
        console.log('Module not found')
        return
      }
      const module = newModules[moduleIndex]
      const topicIndex = module.topics.findIndex((t) => t.id === topicId)
      if (topicIndex === -1) {
        console.log('Topic not found')
        return
      }
      const newTopics = [...module.topics]
      // console.log('newTopics', newTopics)
      newTopics[topicIndex] = data.topic
      module.topics = newTopics
      // console.log('newModules', newModules)
      setModules(newModules)
    },
    [modules],
  )

  const onUpdateLinks = () => {
    setShowUpdateLinksModal(true)
  }

  // uploads a rendered pdf blob to S3 as a topic's print.pdf, via a
  // presigned PUT URL. Shared by the single-topic and dump-everything flows.
  const uploadPrintPdf = async (
    documentId: string,
    blob: Blob,
  ): Promise<void> => {
    const { presignedUrl } = await getPrintPdfPresignedUrl(documentId)
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/pdf' },
      body: blob,
    })
    if (!res.ok) {
      throw new Error(`S3 upload responded with ${res.status}`)
    }
  }

  const onDumpEverything = async () => {
    const topicsWithPosition = modules.flatMap((m, moduleIndex) =>
      m.topics.map((topic, topicIndexInModule) => ({
        topic,
        topicNumber: `${moduleIndex + 1}.${topicIndexInModule + 1}`,
      })),
    )
    if (topicsWithPosition.length === 0) {
      ToastUtil.warning('No topics to export')
      return
    }

    const alreadyHadLink = dumpZipStatus === 'yes'

    setIsDumping(true)
    setDumpProgress({ done: 0, total: topicsWithPosition.length })

    try {
      const zip = new JSZip()
      const invalidLinkTopics: string[] = []
      const failedRenderTopics: string[] = []
      let zippedCount = 0

      // check every topic's print.pdf existence in one batched request
      // up front, rather than one request per topic — the render/upload loop
      // below still has to stay sequential (single headless Chrome instance
      // on the pdf server), but this lookup doesn't.
      const documentIds = topicsWithPosition
        .map(({ topic }) => getDocumentIdFromTopicLink(topic.link))
        .filter((id): id is string => !!id)
      const existsByDocumentId =
        documentIds.length > 0
          ? await checkPrintPdfExistsBatch(documentIds)
          : {}

      let completedCount = 0
      const blobsByTopicId = new Map<string, Blob>()

      // fetching an already-cached print.pdf is a pure read with no
      // shared bottleneck (unlike rendering, below), so do all of those in
      // parallel rather than one at a time.
      const cachedEntries = topicsWithPosition.filter(({ topic }) => {
        if (!isValidTopicLink(topic.link)) return false
        const documentId = getDocumentIdFromTopicLink(topic.link)
        return !!documentId && existsByDocumentId[documentId]
      })
      await Promise.all(
        cachedEntries.map(async ({ topic }) => {
          const documentId = getDocumentIdFromTopicLink(topic.link) as string
          try {
            blobsByTopicId.set(topic.id, await downloadPrintPdf(documentId))
          } catch (err) {
            console.error(
              `Failed to download cached print.pdf for topic "${topic.name}"`,
              err,
            )
          } finally {
            completedCount++
            setDumpProgress({
              done: completedCount,
              total: topicsWithPosition.length,
            })
          }
        }),
      )

      // rendered sequentially, one topic at a time, for whatever's left over
      // (no cached print.pdf). The pdf server runs a single headless Chrome instance,
      // so firing all topics in parallel risks overloading it and hitting race
      // conditions between concurrent page.pdf() calls. Parallelise later once
      // that's been made safe.
      for (let i = 0; i < topicsWithPosition.length; i++) {
        const { topic, topicNumber } = topicsWithPosition[i]
        if (blobsByTopicId.has(topic.id)) {
          continue
        }
        try {
          if (!isValidTopicLink(topic.link)) {
            console.error(
              `Skipping topic "${topic.name}" (${topicNumber}) — invalid link, not sent to pdf server: ${topic.link}`,
            )
            invalidLinkTopics.push(topicNumber)
          } else {
            const documentId = getDocumentIdFromTopicLink(topic.link)
            const blob = await renderUrlToPdf(topic.link)
            if (documentId) {
              await uploadPrintPdf(documentId, blob)
            }
            blobsByTopicId.set(topic.id, blob)
          }
        } catch (err) {
          console.error(
            `Failed to get print.pdf for topic "${topic.name}"`,
            err,
          )
          failedRenderTopics.push(topicNumber)
        }
        completedCount++
        setDumpProgress({
          done: completedCount,
          total: topicsWithPosition.length,
        })
      }

      // assemble the zip in original module.topic order, tracking what went
      // in so the backend can build an index.html linking to each print.pdf
      const zippedTopics: {
        topicNumber: string
        name: string
        documentId: string
      }[] = []
      for (const { topic, topicNumber } of topicsWithPosition) {
        const blob = blobsByTopicId.get(topic.id)
        const documentId = getDocumentIdFromTopicLink(topic.link)
        if (blob && documentId) {
          zip.file(pdfFilenameForTopic(topicNumber, topic), blob)
          zippedCount++
          zippedTopics.push({ topicNumber, name: topic.name, documentId })
        }
      }

      if (
        (invalidLinkTopics.length > 0 || failedRenderTopics.length > 0) &&
        zippedCount > 0
      ) {
        const parts: string[] = []
        if (invalidLinkTopics.length > 0) {
          parts.push(
            `${
              invalidLinkTopics.length
            } with invalid links (${invalidLinkTopics.join(', ')})`,
          )
        }
        if (failedRenderTopics.length > 0) {
          parts.push(
            `${
              failedRenderTopics.length
            } failed to render (${failedRenderTopics.join(', ')})`,
          )
        }
        ToastUtil.warning(`Skipped topic(s): ${parts.join('; ')}`)
      }

      if (zippedCount === 0) {
        ToastUtil.error(
          'Nothing to export — no topics had a valid link or renderable print.pdf',
        )
        return
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // hand the finished zip + an index of what's in it to the
      // backend — it uploads the zip to S3 and generates an index.html
      // linking to every topic's print.pdf and the zip itself. No forced
      // browser download here — the export link is the deliverable.
      try {
        await uploadCourseDump(course.id, zipBlob, {
          courseName: getDumpZipFilename().replace(/\.zip$/, ''),
          topics: zippedTopics,
        })
        setDumpZipStatus('yes')
        if (alreadyHadLink) {
          ToastUtil.success('Course export page refreshed')
        } else {
          await onCopyDumpZipUrl('Export all link generated')
        }
      } catch (err) {
        console.error('Failed to cache dump zip', err)
        ToastUtil.error('Failed to save the export — please try again')
      }
    } catch (err) {
      console.error(err)
      ToastUtil.error('Failed to export course')
    } finally {
      setIsDumping(false)
      setDumpProgress(null)
    }
  }

  const onExportExcel = async () => {
    try {
      const res = await exportArchExcel(
        course.programId,
        course.intakeId,
        course.id,
      )
      const filename = getExportFileNameForCourse(course.name)
      downloadBlobAsFile(res, filename)
    } catch (error) {
      console.log('Error exporting excel', error)
    }
  }

  const onCheckTopics = async () => {
    try {
      setIsCheckingTopics(true)
      const res = await checkTopics(course.id)
      setResult(res)
      setShowResult(true)
      // update check topics result if exists
      setCheckTopicsResult(res)
    } catch (error) {
      ToastUtil.error('Check topics failed')
      console.error(error)
    } finally {
      setIsCheckingTopics(false)
    }
  }

  const onCloseResult = () => {
    setShowResult(false)
    setResult(null)
  }

  const getFailedMessage = (field: string) => {
    const domain = process.env.REACT_APP_PUBLISH_URL
    switch (field) {
      case 'title':
        return 'Topic title wording does not match architecture'
      case 'learningOutcomes':
        return `Topic ${
          isEnterprise ? 'LO' : loLabel.toLowerCase()
        } wording does not match architecture`
      case 'module':
        return 'Topic module title wording does not match architecture'
      case 'duration':
        return 'Topic duration does not match architecture'
      case 'domain':
        return `Topic link is not a ${domain} page`
      default:
        return ''
    }
  }

  useEffect(() => {
    if (isLogged) {
      fetchCourse()
      fetchCheckTopicsResults()
    }
  }, [fetchCheckTopicsResults, fetchCourse, isLogged])

  const onMouseEnterActionsMenu = () => {
    if (isCheckingTopics || isDumping) return
    openActionsMenu()
  }

  const onMouseLeaveActionsMenu = closeActionsMenu

  useEffect(() => {
    Emitter.on(Events.UpdatedArchTopic, onUpdatedArchTopic)
    return () => {
      Emitter.off(Events.UpdatedArchTopic, onUpdatedArchTopic)
    }
  }, [onUpdatedArchTopic])

  // fire-and-forget prewarm of the same endpoint onDumpEverything
  // hits first (print-pdf-exists-batch) — no UI/state tied to the result.
  // lx-engine-backend runs on Lambda, so without this the button's first
  // click after opening the modal eats a cold start; this absorbs it while
  // the user is just looking at the topic list instead.
  useEffect(() => {
    const documentIds = modules
      .flatMap((m) => m.topics)
      .filter((t) => isValidTopicLink(t.link))
      .map((t) => getDocumentIdFromTopicLink(t.link))
      .filter((id): id is string => !!id)
    if (documentIds.length === 0) {
      return
    }
    checkPrintPdfExistsBatch(documentIds).catch(() => {
      // best-effort warmup — a failure here doesn't affect anything the
      // user sees, onDumpEverything will just retry the same call itself
    })
  }, [modules, checkPrintPdfExistsBatch])

  // checks whether the course already has a cached dump.zip, so the
  // DUMP EVERYTHING button can show a generated/not-generated indicator.
  useEffect(() => {
    let cancelled = false
    setDumpZipStatus('checking')

    checkDumpZipExists(course.id)
      .then(({ exists }) => {
        if (!cancelled) {
          setDumpZipStatus(exists ? 'yes' : 'no')
        }
      })
      .catch((err) => {
        console.error('Failed to check dump.zip', err)
        if (!cancelled) {
          setDumpZipStatus('unknown')
        }
      })

    return () => {
      cancelled = true
    }
  }, [course.id, checkDumpZipExists])

  return (
    <>
      <div
        className="absolute z-[999] flex h-full w-full items-center justify-center bg-[#D5D5DBAA]"
        data-testid="modal-arch-course-details-overlay"
      >
        <div
          className="flex h-3/4 w-3/4 flex-col gap-[12px] bg-white p-[24px] shadow-xl"
          data-testid="modal-arch-course-details-container"
        >
          <div className="flex flex-row">
            <div className="grow" />
            <div
              className="cursor-pointer self-end"
              onClick={onClose}
              data-testid="btn-close-arch-course-details"
            >
              <Svg icon={Icon.Close} className={IconClass.FillBlack2} />
            </div>
          </div>
          <div className="flex flex-row">
            {course.intakeLocked ? (
              <Svg icon={Icon.LockLarge} color="#8A68C8" />
            ) : (
              <Svg icon={Icon.Unlock} color="#8A68C8" width={24} height={28} />
            )}
            <div className="w-[12px]" />
            {renderTitle()}
            <div className="grow" />
            <div
              className="relative"
              ref={actionsMenuRef}
              onMouseEnter={onMouseEnterActionsMenu}
              onMouseLeave={onMouseLeaveActionsMenu}
            >
              <button
                className="dropdown-box flex min-h-[36px] w-full min-w-[188px] items-center justify-center border border-primaryGrey px-3"
                style={{
                  paddingRight: 7,
                  cursor: 'pointer',
                  backgroundColor: 'white',
                }}
                disabled={isCheckingTopics || isDumping}
                data-testid="btn-actions-menu"
              >
                <div className="grow" />
                <span className="mentem-dropdown-button mr-4 flex max-w-[400px] items-center justify-center gap-2 whitespace-normal text-center">
                  {(isCheckingTopics || isDumping) && <ButtonSpinner />}
                  {isCheckingTopics && 'Checking topics...'}
                  {isDumping &&
                    (dumpProgress
                      ? `Exporting ${dumpProgress.done}/${dumpProgress.total}`
                      : 'Exporting...')}
                  {!isCheckingTopics && !isDumping && 'Actions'}
                </span>
                <div className="grow" />
                {!isCheckingTopics && !isDumping && (
                  <svg
                    width="14"
                    height="9"
                    viewBox="0 0 14 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ minWidth: '14px', marginTop: '2px' }}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.292893 0.511643C0.653377 0.151159 1.22061 0.12343 1.6129 0.428455L1.70711 0.511643L7 5.80375L12.2929 0.511643C12.6534 0.151159 13.2206 0.12343 13.6129 0.428455L13.7071 0.511643C14.0676 0.872127 14.0953 1.43936 13.7903 1.83165L13.7071 1.92586L7.70711 7.92586C7.34662 8.28634 6.77939 8.31407 6.3871 8.00905L6.29289 7.92586L0.292893 1.92586C-0.0976311 1.53533 -0.0976311 0.902168 0.292893 0.511643Z"
                      fill="#51597B"
                    />
                  </svg>
                )}
              </button>
              {isActionsMenuOpen && (
                <ul
                  className="mentem-scrollbar-2 bg-base-100 absolute left-0 top-full z-50 max-h-[60vh] w-full min-w-[188px] overflow-y-auto bg-white shadow"
                  data-testid="actions-menu"
                >
                  <li
                    className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-center hover:bg-[#FFDC0026]"
                    onClick={() => {
                      forceCloseActionsMenu()
                      onCheckTopics()
                    }}
                    data-testid="btn-check-topics"
                  >
                    Check topics
                  </li>
                  <li
                    className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-center hover:bg-[#FFDC0026]"
                    onClick={() => {
                      forceCloseActionsMenu()
                      onUpdateLinks()
                    }}
                    data-testid="btn-update-links"
                  >
                    Update links
                  </li>
                  <li
                    className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-center hover:bg-[#FFDC0026]"
                    onClick={() => {
                      forceCloseActionsMenu()
                      onExportExcel()
                    }}
                    data-testid="btn-export-excel"
                  >
                    Export Excel
                  </li>
                  <li
                    className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-center hover:bg-[#FFDC0026]"
                    onClick={() => {
                      forceCloseActionsMenu()
                      onDumpEverything()
                    }}
                    data-testid="btn-dump-everything"
                  >
                    Export all
                  </li>
                </ul>
              )}
            </div>
          </div>
          {hasAnyValidTopicLink && dumpZipStatus === 'yes' && (
            <div className="flex items-center gap-1">
              <span data-testid="dump-zip-status-label">
                Course export link:
              </span>
              <a
                href={getDumpIndexUrl()}
                target="_blank"
                rel="noreferrer"
                className="arch-topic-list-item-link"
                data-testid="dump-zip-status-link"
              >
                {getDumpIndexUrl()}
              </a>
              <button
                type="button"
                aria-label="Copy public link"
                onClick={() => onCopyDumpZipUrl()}
                data-testid="dump-zip-copy-link"
              >
                <Svg icon={Icon.Link} className={IconClass.FillTertiaryTeal} />
              </button>
            </div>
          )}
          {isFetchingCourse && <Spinner />}
          {!isFetchingCourse && modules.length === 0 && (
            <div className="flex flex-col items-center justify-center">
              <p className="text-center">
                No {moduleLabel.toLowerCase()}s found
              </p>
            </div>
          )}
          {!isFetchingCourse && modules.length > 0 && (
            <div
              className="mentem-scrollbar-2 overflow-y-auto"
              data-testid="arch-course-details-content"
            >
              <ul className="divide-slate-200 divide-y">
                {modules.map((module, moduleIndex) =>
                  renderModule(module, moduleIndex),
                )}
              </ul>
              {!isFetchingCourse && quizzes.length > 0 && (
                <>
                  <div
                    className="arch-module-list-item flex w-full flex-row items-center justify-between"
                    style={unswoHeaderStyle}
                  >
                    <div className="pl-[40px]">Tests</div>
                    <div className="flex flex-row items-center gap-[12px] pr-[24px]">
                      <button
                        type="button"
                        aria-label={
                          isCollapsedAssessments
                            ? 'Expand tests'
                            : 'Collapse tests'
                        }
                        onClick={() =>
                          setIsCollapsedAssessments(!isCollapsedAssessments)
                        }
                      >
                        {isCollapsedAssessments ? (
                          <Svg
                            icon={Icon.Down}
                            className={headerFillIconClass}
                          />
                        ) : (
                          <Svg icon={Icon.Up} className={headerFillIconClass} />
                        )}
                      </button>
                    </div>
                  </div>
                  {!isCollapsedAssessments && (
                    <ul className="divide-slate-200 divide-y">
                      {quizzes.map((quiz) => renderQuiz(quiz))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {showResult && result && (
        <WidgetArchCheckTopicsResult
          result={result}
          isEnterprise={isEnterprise}
          onClose={onCloseResult}
        />
      )}
      {checkTopicsResult &&
        checkTopicsResult.results &&
        checkTopicsResult.results.map((r) => {
          return (
            <Tooltip
              key={`tooltip-topic-result-${r.topic.id}`}
              id={`tooltip-topic-result-${r.topic.id}`}
              className="mentem-tooltip-large z-[9999]"
              style={tooltipStyle}
              place="right"
              noArrow={true}
            >
              {r.result.success ? (
                `Title, ${
                  isEnterprise ? 'LOs' : 'purposes'
                } and link match architecture`
              ) : (
                <div>
                  <p>
                    <b>Failed:</b>
                  </p>
                  {r.result.validations
                    ?.filter((v) => !v.matched)
                    ?.map((v, index) => (
                      <p key={index}>
                        <>
                          <p>
                            <b>{getFailedMessage(v.field)}</b>
                          </p>
                        </>
                      </p>
                    ))}
                  {r.result.message && (
                    <p>
                      <b>{r.result.message}</b>
                    </p>
                  )}
                </div>
              )}
            </Tooltip>
          )
        })}
      {showUpdateLinksModal && (
        <UpdateTopicLinksModal
          modules={modules}
          isFetchingCourse={isFetchingCourse}
          moduleLabel={moduleLabel}
          onSave={async (editedLinks) => {
            const changed = modules.flatMap((m) =>
              m.topics
                .filter((t) => editedLinks[t.id] !== (t.link ?? ''))
                .map((t) => ({ id: t.id, link: editedLinks[t.id] })),
            )
            await Promise.all(
              changed.map((t) =>
                updateArchTopic(t.id, undefined, undefined, t.link),
              ),
            )
            setModules((prev) =>
              prev.map((m) => ({
                ...m,
                topics: m.topics.map((t) =>
                  editedLinks[t.id] !== undefined
                    ? { ...t, link: editedLinks[t.id] }
                    : t,
                ),
              })),
            )
            setShowUpdateLinksModal(false)
          }}
          onClose={() => setShowUpdateLinksModal(false)}
        />
      )}
    </>
  )
}
